import moment from 'moment-timezone';
import {
  AccountData,
  AreaOccupancyData,
  AreaOccupancySlotData,
  BookingType,
  OrderData,
  SchedulerConfigOccupancyData,
  SchedulerConfigurationData,
  UnavailabilityData,
} from '../../../api';
import { indexesToMask, maskToIndexes, subtractMask } from '../../../utils/maskUtils';
import { blockCoversTime } from './areaBlocks';
import { isProjectSelectionPending } from '../../../common/ProjectSelectionPendingBadge';
import { isPendingOrder } from '../../../common/pendingOrderUtils';
import { DATE_FORMAT } from '../../consts';
import type { AgendaSession } from './agendaSessions';
import { seatedIndexesInConfig } from '../../utils/orderSeating';

export const SECONDS_PER_MINUTE = 60;

export const slotIndexOf = (seconds: number, slotMinutes: number): number =>
  Math.floor(seconds / (slotMinutes * SECONDS_PER_MINUTE));

export const slotStartSeconds = (slot: number, slotMinutes: number): number => slot * slotMinutes * SECONDS_PER_MINUTE;

/** Server-provided slot state. Occupancy is never recomputed on the client. */
export const slotAt = (area: AreaOccupancyData, slot: number): AreaOccupancySlotData | undefined =>
  area.slots.find((candidate) => candidate.slot === slot);

export enum AreaSeverity {
  OK = 'ok',
  WARN = 'warn',
  CRIT = 'crit',
  BLOCKED_ALL = 'blocked_all',
}

export const severityAt = (slotData: AreaOccupancySlotData | undefined): AreaSeverity => {
  if (!slotData) {
    return AreaSeverity.OK;
  }
  if (slotData.total > 0 && slotData.blocked >= slotData.total) {
    return AreaSeverity.BLOCKED_ALL;
  }
  if (slotData.free === 0) {
    return AreaSeverity.CRIT;
  }
  if (slotData.free <= 2) {
    return AreaSeverity.WARN;
  }
  return AreaSeverity.OK;
};

export enum HorizonType {
  CLOSED_OPENS_AT = 'closed_opens_at',
  CLOSED_ALL_DAY = 'closed_all_day',
  FREE = 'free',
  OPENS_AT = 'opens_at',
  FULL_ALL_DAY = 'full_all_day',
  UNKNOWN = 'unknown',
}

export type HorizonResult =
  | { type: HorizonType.CLOSED_OPENS_AT; opensAtSeconds: number }
  | { type: HorizonType.CLOSED_ALL_DAY }
  | { type: HorizonType.FREE; sessionWindowN: number; untilSeconds: number | null }
  | { type: HorizonType.OPENS_AT; atSeconds: number; n: number }
  | { type: HorizonType.FULL_ALL_DAY }
  | { type: HorizonType.UNKNOWN };

type HorizonArgs = {
  area: AreaOccupancyData;
  fromSlot: number;
  sessionSlots: number;
  dayEndSlot: number;
  slotMinutes: number;
};

/**
 * "Can I seat a session, and for how long?" — computed forward from the selected slot, bounded by
 * the config's working hours (never by the raw length of `slots`).
 *
 * A fully blocked area reads "closed", never "full until end of day" (spec §9).
 */
export const horizon = ({ area, fromSlot, sessionSlots, dayEndSlot, slotMinutes }: HorizonArgs): HorizonResult => {
  const current = slotAt(area, fromSlot);
  if (!current) {
    return { type: HorizonType.UNKNOWN };
  }

  const isClosed = (slotData: AreaOccupancySlotData) => slotData.total > 0 && slotData.blocked >= slotData.total;

  if (isClosed(current)) {
    for (let slot = fromSlot + 1; slot <= dayEndSlot; slot++) {
      const candidate = slotAt(area, slot);
      if (candidate && !isClosed(candidate)) {
        return { type: HorizonType.CLOSED_OPENS_AT, opensAtSeconds: slotStartSeconds(slot, slotMinutes) };
      }
    }
    return { type: HorizonType.CLOSED_ALL_DAY };
  }

  if (current.free > 0) {
    let sessionWindowN = current.free;
    const sessionEnd = Math.min(fromSlot + sessionSlots, dayEndSlot + 1);
    for (let slot = fromSlot; slot < sessionEnd; slot++) {
      const candidate = slotAt(area, slot);
      sessionWindowN = Math.min(sessionWindowN, candidate ? candidate.free : 0);
    }

    let untilSeconds: number | null = null;
    for (let slot = fromSlot + 1; slot <= dayEndSlot; slot++) {
      const candidate = slotAt(area, slot);
      if (candidate && candidate.free === 0) {
        untilSeconds = slotStartSeconds(slot, slotMinutes);
        break;
      }
    }
    return { type: HorizonType.FREE, sessionWindowN, untilSeconds };
  }

  for (let slot = fromSlot + 1; slot <= dayEndSlot; slot++) {
    const candidate = slotAt(area, slot);
    if (candidate && candidate.free > 0) {
      return { type: HorizonType.OPENS_AT, atSeconds: slotStartSeconds(slot, slotMinutes), n: candidate.free };
    }
  }
  return { type: HorizonType.FULL_ALL_DAY };
};

const fullName = (order: OrderData) => [order.firstname, order.lastname].filter(Boolean).join(' ');

const NO_SHOW_AFTER_SECONDS = 30 * 60;

export enum AgendaCardState {
  PENDING = 'pending',
  NO_SHOW = 'no_show',
  NOT_ARRIVED = 'not_arrived',
  NEEDS_PROJECT = 'needs_project',
  OPEN_BILL = 'open_bill',
  HERE = 'here',
  DONE = 'done',
}

/** referenceSeconds = dayReferenceSeconds(currentDate, accountToday, nowSeconds) from ./openBills. */
export const cardState = (order: OrderData, account: AccountData, referenceSeconds: number): AgendaCardState => {
  if (isPendingOrder(order)) {
    return AgendaCardState.PENDING;
  }
  const startSeconds = order.time ?? 0;
  const endSeconds = startSeconds + order.duration;
  if (!order.checkin) {
    return referenceSeconds >= startSeconds + NO_SHOW_AFTER_SECONDS
      ? AgendaCardState.NO_SHOW
      : AgendaCardState.NOT_ARRIVED;
  }
  if (isProjectSelectionPending(order, account) || !order.extra_items?.length) {
    return AgendaCardState.NEEDS_PROJECT;
  }
  if (order.balance_closed_at) {
    return AgendaCardState.DONE;
  }
  if ((order.balance_due ?? 0) > 0) {
    return referenceSeconds >= endSeconds ? AgendaCardState.OPEN_BILL : AgendaCardState.HERE;
  }
  return AgendaCardState.DONE;
};

const CARD_STATE_RANK: Record<AgendaCardState, number> = {
  [AgendaCardState.PENDING]: 0,
  [AgendaCardState.NO_SHOW]: 0,
  [AgendaCardState.NOT_ARRIVED]: 0,
  [AgendaCardState.NEEDS_PROJECT]: 1,
  [AgendaCardState.OPEN_BILL]: 2,
  [AgendaCardState.HERE]: 2,
  [AgendaCardState.DONE]: 3,
};

export const cardStateRank = (state: AgendaCardState): number => CARD_STATE_RANK[state];

export const orderIsActiveAt = (order: OrderData, seconds: number): boolean => {
  const start = order.time ?? 0;
  return start <= seconds && seconds < start + order.duration;
};

/**
 * The counter's lookup: a customer says a name or an order number, and the admin has to find which
 * area they are in. Matching is substring on the full name and on the order number, with a leading
 * "#" tolerated because that is how the number is printed on the row.
 */
export const orderMatchesQuery = (order: OrderData, query: string): boolean => {
  const needle = query.trim().replace(/^#/, '').toLowerCase();
  if (!needle) {
    return true;
  }
  const name = `${order.firstname ?? ''} ${order.lastname ?? ''}`.toLowerCase();
  if (name.includes(needle) || String(order.order_number ?? '').includes(needle)) {
    return true;
  }
  const digits = (value: string) => value.replace(/\D/g, '');
  return digits(needle).length >= 3 && digits(order.phone ?? '').includes(digits(needle));
};

/** How many orders holding tables in the area start in each slot, keyed by slot index. */
export const arrivalsBySlot = (
  orders: OrderData[],
  area: AreaOccupancyData,
  config: SchedulerConfigurationData,
  slotMinutes: number,
): Record<number, number> => {
  const areaIndexes = new Set(maskToIndexes(area.mask));
  return orders
    .filter((order) => seatedIndexesInConfig(order, config).some((index) => areaIndexes.has(index)))
    .reduce<Record<number, number>>((counts, order) => {
      const slot = slotIndexOf(order.time ?? 0, slotMinutes);
      counts[slot] = (counts[slot] ?? 0) + 1;
      return counts;
    }, {});
};

/**
 * Resource indexes inside the area that are neither held by an active order nor already blocked at
 * the selected time. The ONLY consumer is partial-block composition — never display.
 */
export const freeBitsAt = (
  area: AreaOccupancyData,
  occupancyConfig: SchedulerConfigOccupancyData,
  config: SchedulerConfigurationData,
  orders: OrderData[],
  dayUnavailabilities: UnavailabilityData[],
  selectedSeconds: number,
): number[] => {
  const resourceCount = occupancyConfig.resource_count;
  const occupiedIndexes = orders
    .filter((order) => orderIsActiveAt(order, selectedSeconds))
    .flatMap((order) => seatedIndexesInConfig(order, config));

  let takenMask = indexesToMask(occupiedIndexes, resourceCount);
  dayUnavailabilities
    .filter((unavailability) => blockCoversTime(unavailability, selectedSeconds))
    .forEach((unavailability) => {
      (unavailability.shared_resources_blocks?.scheduler_configs ?? [])
        .filter((block) => block.scheduler_config_id === occupancyConfig.scheduler_config_id)
        .forEach((block) => {
          takenMask = indexesToMask([...maskToIndexes(takenMask), ...maskToIndexes(block.blocked_mask)], resourceCount);
        });
    });

  return maskToIndexes(subtractMask(area.mask, takenMask));
};

/** Bits to block for a partial close: the N free bits closest to the end of the area. */
export const pickPartialBlockBits = (freeBits: number[], requested: number): number[] => {
  const count = Math.max(0, Math.min(requested, freeBits.length));
  return count === 0 ? [] : freeBits.slice(-count);
};

export const accountSchedulerConfigs = (account: AccountData | undefined): SchedulerConfigurationData[] =>
  account?.shared_resources?.scheduler_configs ?? [];

export const hasSchedulerConfigs = (account: AccountData | undefined): boolean =>
  accountSchedulerConfigs(account).length > 0;

export const sortSlotOrders = (orders: OrderData[], account: AccountData, referenceSeconds: number): OrderData[] =>
  [...orders].sort(
    (a, b) =>
      cardStateRank(cardState(a, account, referenceSeconds)) - cardStateRank(cardState(b, account, referenceSeconds)) ||
      fullName(a).localeCompare(fullName(b)) ||
      (a.order_number ?? 0) - (b.order_number ?? 0),
  );

export type AgendaSlotGroup = {
  slot: number;
  startSeconds: number;
  sessions: AgendaSession[];
  orders: OrderData[];
  persons: number;
};

const sortSessions = (sessions: AgendaSession[]): AgendaSession[] =>
  [...sessions].sort(
    (a, b) =>
      (a.event.time ?? 0) - (b.event.time ?? 0) ||
      (a.event.time ?? 0) + a.event.duration - ((b.event.time ?? 0) + b.event.duration) ||
      a.event.id.localeCompare(b.event.id),
  );

export const groupAgendaBySlot = (
  orders: OrderData[],
  sessions: AgendaSession[],
  slotMinutes: number,
  account: AccountData,
  referenceSeconds: number,
): AgendaSlotGroup[] => {
  const ordersBySlot = new Map<number, OrderData[]>();
  orders.forEach((order) => {
    const slot = slotIndexOf(order.time ?? 0, slotMinutes);
    ordersBySlot.set(slot, [...(ordersBySlot.get(slot) ?? []), order]);
  });
  const sessionsBySlot = new Map<number, AgendaSession[]>();
  sessions.forEach((session) => {
    const slot = slotIndexOf(session.event.time ?? 0, slotMinutes);
    sessionsBySlot.set(slot, [...(sessionsBySlot.get(slot) ?? []), session]);
  });
  const slots = [...new Set([...ordersBySlot.keys(), ...sessionsBySlot.keys()])].sort(
    (firstSlot, secondSlot) => firstSlot - secondSlot,
  );
  return slots.map((slot) => {
    const slotOrders = ordersBySlot.get(slot) ?? [];
    const slotSessions = sessionsBySlot.get(slot) ?? [];
    return {
      slot,
      startSeconds: slotStartSeconds(slot, slotMinutes),
      sessions: sortSessions(slotSessions),
      orders: sortSlotOrders(slotOrders, account, referenceSeconds),
      persons:
        slotOrders.reduce((sum, order) => sum + order.persons, 0) +
        slotSessions.reduce((sum, session) => sum + session.used, 0),
    };
  });
};

export const lateMinutes = (
  order: OrderData,
  state: AgendaCardState,
  nowSeconds: number,
  isToday: boolean,
): number | null => {
  const startSeconds = order.time ?? 0;
  if (isToday && state === AgendaCardState.NOT_ARRIVED && nowSeconds >= startSeconds) {
    return Math.floor((nowSeconds - startSeconds) / SECONDS_PER_MINUTE);
  }
  return null;
};

export const nextUpOrderId = (
  orders: OrderData[],
  states: Record<string, AgendaCardState>,
  nowSeconds: number,
  isToday: boolean,
): string | null => {
  if (!isToday) {
    return null;
  }
  let nextOrder: OrderData | null = null;
  orders.forEach((order) => {
    const startSeconds = order.time ?? 0;
    if (
      states[order.id] === AgendaCardState.NOT_ARRIVED &&
      startSeconds >= nowSeconds &&
      (nextOrder === null || startSeconds < (nextOrder.time ?? 0))
    ) {
      nextOrder = order;
    }
  });
  return (nextOrder as OrderData | null)?.id ?? null;
};

export enum AgendaStatusFilter {
  ALL = 'all',
  NOT_ARRIVED = 'not_arrived',
  HERE = 'here',
  OPEN_BILL = 'open_bill',
  NO_TABLE = 'no_table',
}

export const matchesStatusFilter = (
  state: AgendaCardState,
  isUnassigned: boolean,
  filter: AgendaStatusFilter,
): boolean => {
  if (filter === AgendaStatusFilter.NOT_ARRIVED) {
    return state === AgendaCardState.NO_SHOW;
  }
  if (filter === AgendaStatusFilter.HERE) {
    return [AgendaCardState.NEEDS_PROJECT, AgendaCardState.HERE, AgendaCardState.OPEN_BILL].includes(state);
  }
  if (filter === AgendaStatusFilter.OPEN_BILL) {
    return state === AgendaCardState.OPEN_BILL;
  }
  if (filter === AgendaStatusFilter.NO_TABLE) {
    return isUnassigned;
  }
  return true;
};

export const statusFilterCounts = (
  orders: OrderData[],
  states: Record<string, AgendaCardState>,
  unassignedIds: Set<string>,
): Record<AgendaStatusFilter, number> => {
  const filters = Object.values(AgendaStatusFilter);
  return Object.fromEntries(
    filters.map((filter) => [
      filter,
      orders.filter((order) => matchesStatusFilter(states[order.id], unassignedIds.has(order.id), filter)).length,
    ]),
  ) as Record<AgendaStatusFilter, number>;
};

export const isNewToday = (order: OrderData, accountToday: string, timezone: string): boolean =>
  order.booking_type !== BookingType.WALK_IN &&
  moment(order.created_at).tz(timezone).format(DATE_FORMAT) === accountToday;

export const elapsedMinutesSinceCheckin = (order: OrderData, nowMoment: moment.Moment): number | null =>
  order.checkin?.created_at ? Math.max(0, nowMoment.diff(moment.utc(order.checkin.created_at), 'minutes')) : null;
